import React, { useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { useTranslation } from "react-i18next";
import HamburgerMenuData from "./HamburgerMenuData";
import Language from "../../../components/account/Language";

const slideInFromLeft = keyframes`
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(0);
  }
`;

const slideOutLeft = keyframes`
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(-100%);
  }
`;
const ContainerX = styled.div``;
const Container = styled.div`
  margin-top: var(--spacing-xxs);
  background-color: rgba(15, 17, 20, 0.55);
  position: fixed;
  right: 0;
  top: 0;
  z-index: var(--zindex-modal);
  min-width: 100%;
  height: 100vh;

  box-sizing: border-box;
  overflow-x: hidden;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  transition: opacity 300ms ease-in-out;
  visibility: ${(props) => (props.$shouldBeVisible ? "visible" : "hidden")};
`;
const Wrapper = styled.div`
  background-color: var(--bg-100);

  width: 90%;
  max-width: 360px;
  height: 100%;
  min-height: 100%;
  position: fixed;
  left: 0;
  top: 0;
  transform: translateX(100%);
  transition: transform 0.3s ease-in-out;
  box-shadow: var(--shadow-large);
  border-radius: 0 var(--border-radius-large) var(--border-radius-large) 0;
  overflow: hidden;
  ${({ $isOpen }) =>
    $isOpen
      ? css`
          animation: ${slideInFromLeft} 0.5s ease-out forwards; // Duže trajanje za slide-in
        `
      : css`
          animation: ${slideOutLeft} 0.2s ease-in forwards; // Kraće trajanje za slide-out
        `}
  @media (min-width: 768px) and (max-width: 1040px) {
    width: 60%;
    max-width: 420px;
  }
`;
// Styled komponenta za hamburger linije
const HamburgerIcon = styled.div`
  width: var(--font-size-xlarge);
  height: 3px;
  background-color: var(--text-200);
  transition: all 0.5s ease-in-out;
  transform-origin: 1px;

  &:nth-child(1) {
    transform: ${({ $isOpen }) => ($isOpen ? "rotate(45deg)" : "rotate(0)")};
    width: 25px;
  }

  &:nth-child(2) {
    transform: ${({ $isOpen }) =>
      $isOpen ? "translateX(100%)" : "translateX(0)"};
    opacity: ${({ $isOpen }) => ($isOpen ? 0 : 1)};
  }

  &:nth-child(3) {
    transform: ${({ $isOpen }) => ($isOpen ? "rotate(-45deg)" : "rotate(0)")};
    width: 25px;
  }
`;
const HamburgerMidIcon = styled(HamburgerIcon)`
  width: calc(var(--font-size-xlarge) - 5px);
  height: 2.8px;
`;

// Styled komponenta za kontejner koji drži hamburger ikonu
const HamburgerContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  height: 25px;
  cursor: pointer;
`;
const XDiv = styled.div`
  padding: 0 var(--spacing-md);
  height: var(--navbar-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--bg-300);
  margin-bottom: var(--spacing-sm);
`;
const MiddleDiv = styled.div``;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;
const IconDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: var(--spacing-sm);
  margin: calc(var(--spacing-sm) * -1);
  border-radius: 50%;
  transition: background-color 0.15s ease-in-out;

  &:hover,
  &:focus-visible {
    background-color: var(--bg-200);
  }
`;

const HamburgerMenuLeft = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [shouldBeVisible, setShouldBeVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldBeVisible(true);
    } else {
      const timer = setTimeout(() => {
        setShouldBeVisible(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <ContainerX>
      <HamburgerContainer
        onClick={toggleMenu}
        role="button"
        tabIndex={0}
        aria-label={t(isOpen ? "ARIA.CLOSE_MENU" : "ARIA.OPEN_MENU")}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleMenu();
          }
        }}
      >
        <HamburgerIcon key="1" $isOpen={isOpen} />
        <HamburgerMidIcon key="2" $isOpen={isOpen} />
        <HamburgerIcon key="3" $isOpen={isOpen} />
      </HamburgerContainer>

      <Container
        onClick={toggleMenu}
        $isOpen={isOpen}
        $shouldBeVisible={shouldBeVisible}
      >
        <Wrapper
          $isOpen={isOpen}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <XDiv>
            <HeaderLeft>
              <Language />
            </HeaderLeft>
            <IconDiv>
              <svg
                width="30px"
                height="30px"
                viewBox="0 0 1024 1024"
                xmlns="http://www.w3.org/2000/svg"
                onClick={toggleMenu}
                role="button"
                tabIndex={0}
                aria-label={t("ARIA.CLOSE_MENU")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleMenu();
                  }
                }}
              >
                <path
                  fill="var(--text-200)"
                  d="M195.2 195.2a64 64 0 0 1 90.496 0L512 421.504 738.304 195.2a64 64 0 0 1 90.496 90.496L602.496 512 828.8 738.304a64 64 0 0 1-90.496 90.496L512 602.496 285.696 828.8a64 64 0 0 1-90.496-90.496L421.504 512 195.2 285.696a64 64 0 0 1 0-90.496z"
                />
              </svg>
            </IconDiv>
          </XDiv>
          <MiddleDiv>
            <HamburgerMenuData setIsOpen={setIsOpen} />
          </MiddleDiv>
        </Wrapper>
      </Container>
    </ContainerX>
  );
};

export default HamburgerMenuLeft;
